import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, fmtDateTime } from "@/lib/format";
import { PageHeader, ConfirmDialog, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Landmark, Loader2 } from "lucide-react";

const ROLES = ["admin", "operateur", "consultation"];
const EMPTY = { name: "", email: "", password: "", role: "consultation", active: true };

export default function Utilisateurs() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/users");
    setRows(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, active: u.active }); setDialog(true); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom obligatoire"); return; }
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        if (!form.email.trim() || !form.password) { toast.error("Email et mot de passe obligatoires"); return; }
        await api.post("/users", { name: form.name, email: form.email, password: form.password, role: form.role });
      }
      toast.success(editing ? "Utilisateur modifié" : "Utilisateur créé");
      setDialog(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/users/${confirm.id}`);
      toast.success("Utilisateur supprimé");
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setConfirm(null);
    }
  };

  return (
    <div data-testid="utilisateurs-page">
      <PageHeader title="Utilisateurs" subtitle="Gestion des accès et des niveaux de permission">
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn">
          <Plus className="mr-1.5 h-4 w-4" /> Nouvel utilisateur
        </Button>
      </PageHeader>

      <Card className="border-slate-200 shadow-sm">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Landmark} title="Aucun utilisateur" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Créé le</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((u) => (
                <TableRow key={u.id} className="hover:bg-slate-50" data-testid="user-row">
                  <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                      {ROLE_LABELS[u.role]}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${u.active ? "text-emerald-600" : "text-red-500"}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${u.active ? "bg-emerald-500" : "bg-red-500"}`} />
                      {u.active ? "Actif" : "Désactivé"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtDateTime(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid="edit-user-btn">
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                    {u.id !== me.id && (
                      <Button variant="ghost" size="icon" onClick={() => setConfirm(u)} data-testid="delete-user-btn">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nom complet *</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setField("email", e.target.value)} data-testid="user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "Nouveau mot de passe (laisser vide pour conserver)" : "Mot de passe *"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setField("password", e.target.value)} data-testid="user-password" />
            </div>
            <div className="space-y-1.5">
              <Label>Rôle *</Label>
              <Select value={form.role} onValueChange={(v) => setField("role", v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <Label>Compte actif</Label>
                <Switch checked={form.active} onCheckedChange={(v) => setField("active", v)} data-testid="user-active" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Annuler</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-user-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Supprimer l'utilisateur ?"
        description={`« ${confirm?.name} » sera définitivement supprimé.`}
        onConfirm={remove}
      />
    </div>
  );
}
